import type { FC } from 'react';
import React from 'react';
import { styled } from '@compiled/react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { token } from '@atlaskit/tokens';

import {
	SPACE_DIRECTORY_YOUR_SPACES_EXPERIENCE,
	ExperienceStart,
} from '@confluence/experience-tracker';
import { useSessionData } from '@confluence/session-data';
import { PerformanceStart, PERFORMANCE_SUBJECT_spaceDirectory } from '@confluence/performance';
import { GenericErrorBoundary, Attribution } from '@confluence/error-boundary';
import { DocumentContentTitle } from '@confluence/document-title';
import { CreateSpaceButton } from '@confluence/create-space/entry-points/createSpace';

import { SearchFilter, LabelsFilter, PillFilters, FiltersContextProvider } from './Filters';
import { Table } from './Table';
import { ErrorMessage } from './ErrorMessage';
import { RecentSpacesSection } from './RecentSpacesSection';

const i18n = defineMessages({
	title: {
		id: 'space-directory.title',
		defaultMessage: 'Spaces',
		description: 'Title of the Space directory screen',
	},
	allSpaces: {
		id: 'space-directory.all-spaces',
		defaultMessage: 'All spaces',
		description:
			'Heading which appears above the table (and filters) of Space directory. The table contains a list of all spaces by default.',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpaceDirectoryContainer = styled.main({
	width: '100%',
	maxWidth: '1000px',
	padding: '0 41px',
	margin: '0 auto',
	boxSizing: 'border-box',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AllSpacesRegion = styled.div({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginTop: '27px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingContainer = styled.div({
	width: '100%',
	height: '32px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginTop: '28px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginBottom: '33px',
	display: 'flex',
	justifyContent: 'space-between',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Heading = styled.h1({
	fontSize: '29px',
	fontWeight: 500,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FiltersContainer = styled.div({
	width: '100%',
	'@media only screen and (min-width: 1050px)': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'&': {
			display: 'flex',
			justifyContent: 'space-between',
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleAndCategory = styled.div({
	display: 'flex',
	alignItems: 'flex-end',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginBottom: '17px',
	flexGrow: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PillsContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginBottom: '17px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginLeft: '9px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingTop: '22px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AllSpacesHeading = styled.h2({
	fontSize: '24px',
	fontWeight: 500,
	marginBottom: token('space.200', '16px'),
	lineHeight: 'inherit',
	letterSpacing: 'inherit',
});

export const SpaceDirectory: FC = () => {
	const intl = useIntl();
	const { isLicensed } = useSessionData();

	return (
		<>
			<PerformanceStart
				subject={PERFORMANCE_SUBJECT_spaceDirectory}
				subjectId="initial"
				markBeforeMount
			/>
			{isLicensed && <ExperienceStart name={SPACE_DIRECTORY_YOUR_SPACES_EXPERIENCE} />}
			<DocumentContentTitle titlePrefix={intl.formatMessage(i18n.title)} />
			<SpaceDirectoryContainer>
				<HeadingContainer>
					<Heading>
						<FormattedMessage {...i18n.title} />
					</Heading>
					<CreateSpaceButton source="spaceDirectoryScreen" appearance="default" />
				</HeadingContainer>
				<GenericErrorBoundary
					attribution={Attribution.DISCO}
					renderOnError={() => <ErrorMessage />}
				>
					{isLicensed && <RecentSpacesSection />}
					<AllSpacesRegion role="region" aria-label={intl.formatMessage(i18n.allSpaces)}>
						{isLicensed && (
							<AllSpacesHeading>
								<FormattedMessage {...i18n.allSpaces} />
							</AllSpacesHeading>
						)}
						<FiltersContextProvider>
							<FiltersContainer>
								<TitleAndCategory>
									<SearchFilter />
									{isLicensed && <LabelsFilter />}
								</TitleAndCategory>
								{isLicensed && (
									<PillsContainer>
										<PillFilters />
									</PillsContainer>
								)}
							</FiltersContainer>
							<Table />
						</FiltersContextProvider>
					</AllSpacesRegion>
				</GenericErrorBoundary>
			</SpaceDirectoryContainer>
		</>
	);
};
